import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { BackendApiService } from "../../../app/services/backend-api.service";
import { BaseUrlConstantsService } from "../../../app/services/base-url-constants.service";

@Component({
  selector: "app-cylinder-modal",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: "./cylinder-modal.component.html",
  styleUrl: "./cylinder-modal.component.scss",
})
export class CylinderModalComponent {
  @Input("data") formData: any;
  @Output("closeModal") closeModal = new EventEmitter<boolean>();
  public cylinderBaseUrl=BaseUrlConstantsService.CYLINDER_BASE_URL
  thresholdForm: FormGroup;

  constructor(private fb: FormBuilder, private srv: BackendApiService) {}

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    const { flow, pressure } = this.formData || {};
    this.thresholdForm = this.fb.group({
      flow: this.fb.group(
        {
          baseValue: [
            flow.base_value,
            [Validators.required, Validators.min(0)],
          ],
          maxValue: [flow.max_value, [Validators.required, Validators.min(0)]],
          green: [
            flow.threshold_1,
            [Validators.required, Validators.min(0)],
          ],
          yellow: [
            flow.threshold_2,
            [Validators.required, Validators.min(0)],
          ],
          red: [
            flow.threshold_3,
            [Validators.required, Validators.min(0)],
          ],
        },
        { validators: this.validateThresholds }
      ),

      pressure: this.fb.group(
        {
          baseValue: [
            pressure.base_value,
            [Validators.required, Validators.min(0)],
          ],
          maxValue: [
            pressure.max_value,
            [Validators.required, Validators.min(0)],
          ],
          green: [
            pressure.threshold_1,
            [Validators.required, Validators.min(0)],
          ],
          yellow: [
            pressure.threshold_2,
            [Validators.required, Validators.min(0)],
          ],
          red: [
            pressure.threshold_3,
            [Validators.required, Validators.min(0)],
          ],
        },
        { validators: this.validateThresholds }
      ),
    });
  }

  validateThresholds(group: FormGroup): { [key: string]: any } | null {
    const baseValue = group.get("baseValue")?.value;
    const maxValue = group.get("maxValue")?.value;
    const green = group.get("green")?.value;
    const yellow = group.get("yellow")?.value;
    const red = group.get("red")?.value;

    let errors: { [key: string]: any } = {};

    // Check if baseValue is less than maxValue
    if (baseValue >= maxValue) {
      errors["baseGreaterThanMax"] = true;
    }

    // Check if thresholds are in ascending order: green < yellow < red
    if (green >= yellow) {
      errors["greenNotLessThanYellow"] = true;
    }

    if (yellow >= red) {
      errors["yellowNotLessThanRed"] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  getFormControlError(controlPath: string): string {
    const control = this.thresholdForm.get(controlPath);
    if (!control || !control.errors || !control.touched) return "";

    if (control.errors["required"]) return "This field is required";
    if (control.errors["min"])
      return "Value must be greater than or equal to 0";
    if (control.errors["max"]) return "Value must be less than or equal to 100";

    return "";
  }

  getFormGroupError(groupPath: string): string {
    const group = this.thresholdForm.get(groupPath);
    if (!group || !group.errors) return "";

    if (group.errors["baseGreaterThanMax"])
      return "Base value must be less than max value";
    if (group.errors["greenNotLessThanYellow"])
      return "Green threshold must be less than yellow";
    if (group.errors["yellowNotLessThanRed"])
      return "Yellow threshold must be less than red";

    return "";
  }

  async onSubmit() {
    if (this.thresholdForm.invalid) {
      this.thresholdForm.markAllAsTouched();
      return;
    }
    try {
      const flowPayload = {
        threshold_1: this.thresholdForm.value.flow.green,
        threshold_2: this.thresholdForm.value.flow.yellow,
        threshold_3: this.thresholdForm.value.flow.red,
        max_value: this.thresholdForm.value.flow.maxValue,
        base_value: this.thresholdForm.value.flow.baseValue,
      };
  
      const pressurePayload = {
        threshold_1: this.thresholdForm.value.pressure.green,
        threshold_2: this.thresholdForm.value.pressure.yellow,
        threshold_3: this.thresholdForm.value.pressure.red,
        max_value: this.thresholdForm.value.pressure.maxValue,
        base_value: this.thresholdForm.value.pressure.baseValue,
      };
  
      const [flowRes, pressureRes]:any[] = await Promise.all([
        this.srv.putData(`${this.cylinderBaseUrl}/cylinder/gauge/flow/${this.formData.asset_id}/${this.formData.subsystem_id}/${this.formData.client_id}`, flowPayload),
        this.srv.putData(`${this.cylinderBaseUrl}/cylinder/gauge/pressure/${this.formData.asset_id}/${this.formData.subsystem_id}/${this.formData.client_id}`, pressurePayload),
        // this.srv.putData("http://localhost:7778/cylinder/gauge/flow/1014", flowPayload),
        // this.srv.putData("http://localhost:7778/cylinder/gauge/pressure/1014", pressurePayload),
      ]);
  
      const flowSuccess = flowRes?.data?.code === 200 && flowRes.data.status === "success";
      const pressureSuccess = pressureRes.data.code === 200 && pressureRes.data.status === "success";
  
      if (flowSuccess && pressureSuccess) {
        this.srv.showSuccess("Thresholds updated successfully");
        this.closeModal.emit(true);
      } else {
        this.srv.showError("Error updating one or more thresholds");
      }
    } catch (err) {
      this.srv.showError("Error updating thresholds");
      console.error(err);
    }
  }

  onCancel(): void {
    // Add your cancel logic here
    this.closeModal.emit(false);
  }
}
